<template>
  <div class="third-party-api-container">
    <Card>
      <div class="search-bar">
        <Input
          v-model="keyword"
          placeholder="请输入接口名称、描述或URL关键字搜索"
          clearable
          style="width: 300px; margin-right: 10px"
          @on-enter="handleSearch"
        />
        <Button type="primary" @click="handleSearch" icon="ios-search">搜索</Button>
        <Button type="success" @click="handleAdd" icon="ios-add" style="margin-left: 10px">新增配置</Button>
      </div>

      <Table
        :columns="columns"
        :data="tableData"
        :loading="tableLoading"
        style="margin-top: 20px"
      >
        <template slot-scope="{ row }" slot="method">
          <Tag :color="getMethodColor(row.method)">{{ row.method }}</Tag>
        </template>

        <template slot-scope="{ row }" slot="status">
          <i-switch
            v-model="row.status"
            :true-value="1"
            :false-value="0"
            @on-change="handleStatusChange(row)"
          >
            <span slot="open">启用</span>
            <span slot="close">禁用</span>
          </i-switch>
        </template>

        <template slot-scope="{ row }" slot="create_time">
          {{ formatDate(row.create_time) }}
        </template>

        <template slot-scope="{ row }" slot="action">
          <Button type="info" size="small" @click="handleTest(row)" style="margin-right: 5px">测试</Button>
          <Button type="primary" size="small" @click="handleEdit(row)" style="margin-right: 5px">编辑</Button>
          <Button type="error" size="small" @click="handleDelete(row)">删除</Button>
        </template>
      </Table>

      <div style="margin-top: 20px; text-align: right">
        <Page
          :total="total"
          :page-size="pageSize"
          :current="currentPage"
          show-total
          show-elevator
          show-sizer
          @on-change="handlePageChange"
          @on-page-size-change="handlePageSizeChange"
        />
      </div>
    </Card>

    <!-- 新增/编辑表单弹窗 -->
    <Modal
      v-model="formModal"
      :title="isEdit ? '编辑配置' : '新增配置'"
      width="800"
      :mask-closable="false"
      @on-cancel="handleFormCancel"
    >
      <Form ref="apiForm" :model="formData" :rules="formRules" :label-width="100">
        <FormItem label="接口名称" prop="name">
          <Input v-model="formData.name" placeholder="请输入接口名称（唯一）" />
        </FormItem>

        <FormItem label="接口描述" prop="description">
          <Input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            placeholder="请输入接口描述"
          />
        </FormItem>

        <FormItem label="API地址" prop="url">
          <Input v-model="formData.url" placeholder="请输入完整的API地址" />
        </FormItem>

        <FormItem label="请求方式" prop="method">
          <Select v-model="formData.method" placeholder="请选择请求方式">
            <Option value="GET">GET</Option>
            <Option value="POST">POST</Option>
            <Option value="PUT">PUT</Option>
            <Option value="DELETE">DELETE</Option>
          </Select>
        </FormItem>

        <FormItem label="请求头" prop="headers">
          <Input
            v-model="formData.headers"
            type="textarea"
            :rows="4"
            placeholder='请输入JSON格式的请求头，例如：{"Content-Type": "application/json"}'
          />
          <div style="margin-top: 5px; color: #999; font-size: 12px">
            JSON格式，示例：{"Content-Type": "application/json", "Authorization": "Bearer token"}
          </div>
        </FormItem>

        <FormItem label="默认参数" prop="params">
          <Input
            v-model="formData.params"
            type="textarea"
            :rows="4"
            placeholder='请输入JSON格式的默认参数，例如：{"key": "value"}'
          />
          <div style="margin-top: 5px; color: #999; font-size: 12px">
            JSON格式，示例：{"param1": "value1", "param2": "value2"}
          </div>
        </FormItem>

        <Row>
          <Col span="12">
            <FormItem label="超时时间" prop="timeout">
              <InputNumber
                v-model="formData.timeout"
                :min="1"
                :max="300"
                placeholder="秒"
                style="width: 100%"
              />
              <div style="margin-top: 5px; color: #999; font-size: 12px">单位：秒（默认30秒）</div>
            </FormItem>
          </Col>
          <Col span="12">
            <FormItem label="重试次数" prop="retry_times">
              <InputNumber
                v-model="formData.retry_times"
                :min="0"
                :max="10"
                placeholder="次"
                style="width: 100%"
              />
              <div style="margin-top: 5px; color: #999; font-size: 12px">失败后重试次数（默认3次）</div>
            </FormItem>
          </Col>
        </Row>

        <FormItem label="重试间隔（秒）" prop="retry_interval">
          <InputNumber
            v-model="formData.retry_interval"
            :min="1"
            :max="60"
            placeholder="重试间隔"
            style="width: 100%"
          />
          <div style="margin-top: 5px; color: #999; font-size: 12px">
            两次重试之间的间隔时间，默认1秒
          </div>
        </FormItem>

        <FormItem label="状态" prop="status">
          <i-switch v-model="formData.status" :true-value="1" :false-value="0">
            <span slot="open">启用</span>
            <span slot="close">禁用</span>
          </i-switch>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="handleFormCancel">取消</Button>
        <Button type="primary" :loading="formLoading" @click="handleFormSubmit">保存</Button>
      </div>
    </Modal>

    <!-- 测试弹窗 -->
    <Modal
      v-model="testModal"
      title="测试API配置"
      width="900"
      :mask-closable="false"
      @on-cancel="handleTestCancel"
    >
      <div class="test-container">
        <div class="test-config">
          <h3 style="margin-bottom: 15px">配置信息</h3>
          <Form :label-width="100">
            <FormItem label="接口名称">
              <span>{{ testData.name }}</span>
            </FormItem>
            <FormItem label="API地址">
              <Input v-model="testData.url" placeholder="API地址" />
            </FormItem>
            <FormItem label="请求方式">
              <Select v-model="testData.method" style="width: 150px">
                <Option value="GET">GET</Option>
                <Option value="POST">POST</Option>
                <Option value="PUT">PUT</Option>
                <Option value="DELETE">DELETE</Option>
              </Select>
            </FormItem>
            <FormItem label="请求头">
              <Input
                v-model="testData.headers"
                type="textarea"
                :rows="3"
                placeholder="JSON格式"
              />
            </FormItem>
            <FormItem label="请求参数">
              <Input
                v-model="testData.params"
                type="textarea"
                :rows="3"
                placeholder="JSON格式"
              />
            </FormItem>
            <Row>
              <Col span="12">
                <FormItem label="超时时间">
                  <InputNumber v-model="testData.timeout" :min="1" :max="300" style="width: 100%" />
                  <span style="margin-left: 5px">秒</span>
                </FormItem>
              </Col>
              <Col span="12">
                <FormItem label="重试次数">
                  <InputNumber v-model="testData.retry_times" :min="0" :max="10" style="width: 100%" />
                  <span style="margin-left: 5px">次</span>
                </FormItem>
              </Col>
            </Row>
            <FormItem>
              <Button type="primary" :loading="testLoading" @click="handleTestSubmit">发送测试请求</Button>
            </FormItem>
          </Form>
        </div>

        <div class="test-result" v-if="testResult">
          <h3 style="margin-bottom: 15px">测试结果</h3>
          <div class="result-item">
            <span class="result-label">响应状态：</span>
            <Tag :color="testResult.success ? 'success' : 'error'">
              {{ testResult.success ? '成功' : '失败' }}
            </Tag>
          </div>
          <div class="result-item" v-if="testResult.status_code">
            <span class="result-label">状态码：</span>
            <span>{{ testResult.status_code }}</span>
          </div>
          <div class="result-item" v-if="testResult.response_time">
            <span class="result-label">响应时间：</span>
            <span>{{ testResult.response_time }} ms</span>
          </div>
          <div class="result-item">
            <span class="result-label">响应数据：</span>
          </div>
          <div class="result-data">
            <pre>{{ formatJson(testResult.data || testResult.error || testResult.message) }}</pre>
          </div>
        </div>
      </div>

      <div slot="footer">
        <Button @click="handleTestCancel">关闭</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getThirdPartyApiList,
  createThirdPartyApi,
  updateThirdPartyApi,
  deleteThirdPartyApi,
  testThirdPartyApi
} from '@/api/third-party-api'
import dayjs from 'dayjs'

export default {
  name: 'ThirdPartyApi',
  data () {
    // 验证JSON格式
    const validateJson = (rule, value, callback) => {
      if (!value) {
        callback()
        return
      }
      try {
        JSON.parse(value)
        callback()
      } catch (e) {
        callback(new Error('请输入正确的JSON格式'))
      }
    }

    // 验证URL格式
    const validateUrl = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入API地址'))
        return
      }
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
      if (!urlPattern.test(value) && !value.startsWith('http://') && !value.startsWith('https://')) {
        callback(new Error('请输入有效的URL地址'))
      } else {
        callback()
      }
    }

    return {
      keyword: '',
      tableData: [],
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      columns: [
        {
          title: '接口名称',
          key: 'name',
          width: 150
        },
        {
          title: '接口描述',
          key: 'description',
          ellipsis: true,
          tooltip: true
        },
        {
          title: 'API地址',
          key: 'url',
          ellipsis: true,
          tooltip: true,
          width: 200
        },
        {
          title: '请求方式',
          slot: 'method',
          width: 100,
          align: 'center'
        },
        {
          title: '状态',
          slot: 'status',
          width: 100,
          align: 'center'
        },
        {
          title: '创建时间',
          slot: 'create_time',
          width: 160
        },
        {
          title: '操作',
          slot: 'action',
          width: 220,
          align: 'center'
        }
      ],
      // 表单相关
      formModal: false,
      isEdit: false,
      formLoading: false,
      formData: {
        name: '',
        description: '',
        url: '',
        method: 'GET',
        headers: '{}',
        params: '{}',
        timeout: 30,
        retry_times: 3,
        retry_interval: 1,
        status: 1
      },
      formRules: {
        name: [
          { required: true, message: '请输入接口名称', trigger: 'blur' }
        ],
        url: [
          { required: true, validator: validateUrl, trigger: 'blur' }
        ],
        method: [
          { required: true, message: '请选择请求方式', trigger: 'change' }
        ],
        headers: [
          { validator: validateJson, trigger: 'blur' }
        ],
        params: [
          { validator: validateJson, trigger: 'blur' }
        ]
      },
      // 测试相关
      testModal: false,
      testLoading: false,
      testData: {
        name: '',
        url: '',
        method: 'GET',
        headers: '{}',
        params: '{}',
        timeout: 30,
        retry_times: 3
      },
      testResult: null
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    // 加载列表数据
    async loadData () {
      this.tableLoading = true
      try {
        const res = await getThirdPartyApiList(this.currentPage, this.pageSize, this.keyword)
        if (res.data && res.data.code === 0) {
          this.tableData = res.data.data.list || []
          this.total = res.data.data.total || 0
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载数据失败')
        }
      } catch (error) {
        this.$Message.error('加载数据失败：' + (error.message || '未知错误'))
      } finally {
        this.tableLoading = false
      }
    },

    // 搜索
    handleSearch () {
      this.currentPage = 1
      this.loadData()
    },

    // 分页变化
    handlePageChange (page) {
      this.currentPage = page
      this.loadData()
    },

    handlePageSizeChange (pageSize) {
      this.pageSize = pageSize
      this.currentPage = 1
      this.loadData()
    },

    // 新增
    handleAdd () {
      this.isEdit = false
      this.formData = {
        name: '',
        description: '',
        url: '',
        method: 'GET',
        headers: '{}',
        params: '{}',
        timeout: 30,
        retry_times: 3,
        retry_interval: 1,
        status: 1
      }
      this.formModal = true
    },

    // 编辑
    handleEdit (row) {
      this.isEdit = true
      this.formData = {
        id: row.id,
        name: row.name,
        description: row.description || '',
        url: row.url,
        method: row.method,
        headers: typeof row.headers === 'string' ? row.headers : JSON.stringify(row.headers, null, 2),
        params: typeof row.params === 'string' ? row.params : JSON.stringify(row.params, null, 2),
        timeout: row.timeout || 30,
        retry_times: row.retry_times !== undefined ? row.retry_times : 3,
        retry_interval: row.retry_interval !== undefined ? row.retry_interval : 1,
        status: row.status
      }
      this.formModal = true
    },

    // 删除
    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除配置"${row.name}"吗？`,
        onOk: async () => {
          try {
            const res = await deleteThirdPartyApi(row.id)
            if (res.data && res.data.code === 0) {
              this.$Message.success('删除成功')
              this.loadData()
            } else {
              this.$Message.error((res.data && res.data.msg) || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败：' + (error.message || '未知错误'))
          }
        }
      })
    },

    // 状态变化
    async handleStatusChange (row) {
      const oldStatus = row.status === 1 ? 0 : 1
      try {
        const data = {
          id: row.id,
          status: row.status
        }
        const res = await updateThirdPartyApi(row.id, data)
        if (res.data && res.data.code === 0) {
          this.$Message.success('状态更新成功')
        } else {
          this.$Message.error((res.data && res.data.msg) || '状态更新失败')
          row.status = oldStatus
        }
      } catch (error) {
        this.$Message.error('状态更新失败：' + (error.message || '未知错误'))
        row.status = oldStatus
      }
    },

    // 表单提交
    handleFormSubmit () {
      this.$refs.apiForm.validate(async valid => {
        if (valid) {
          this.formLoading = true
          try {
            const submitData = {
              ...this.formData,
              headers: JSON.parse(this.formData.headers || '{}'),
              params: JSON.parse(this.formData.params || '{}')
            }

            let res
            if (this.isEdit) {
              res = await updateThirdPartyApi(this.formData.id, submitData)
            } else {
              res = await createThirdPartyApi(submitData)
            }

            if (res.data && res.data.code === 0) {
              this.$Message.success(this.isEdit ? '更新成功' : '创建成功')
              this.formModal = false
              this.loadData()
            } else {
              this.$Message.error((res.data && res.data.msg) || (this.isEdit ? '更新失败' : '创建失败'))
            }
          } catch (error) {
            this.$Message.error((this.isEdit ? '更新' : '创建') + '失败：' + (error.message || '未知错误'))
          } finally {
            this.formLoading = false
          }
        }
      })
    },

    // 表单取消
    handleFormCancel () {
      this.formModal = false
      this.$refs.apiForm.resetFields()
    },

    // 测试
    handleTest (row) {
      this.testData = {
        name: row.name,
        url: row.url,
        method: row.method,
        headers: typeof row.headers === 'string' ? row.headers : JSON.stringify(row.headers, null, 2),
        params: typeof row.params === 'string' ? row.params : JSON.stringify(row.params, null, 2),
        timeout: row.timeout || 30,
        retry_times: row.retry_times !== undefined ? row.retry_times : 3
      }
      this.testResult = null
      this.testModal = true
    },

    // 发送测试请求
    async handleTestSubmit () {
      this.testLoading = true
      try {
        const testParams = {
          url: this.testData.url,
          method: this.testData.method,
          headers: JSON.parse(this.testData.headers || '{}'),
          params: JSON.parse(this.testData.params || '{}'),
          timeout: this.testData.timeout,
          retry_times: this.testData.retry_times
        }
        const res = await testThirdPartyApi(testParams)
        if (res.data && res.data.code === 0) {
          this.testResult = res.data.data
          this.$Message.success('测试完成')
        } else {
          this.testResult = { error: (res.data && res.data.msg) || '测试失败' }
          this.$Message.error((res.data && res.data.msg) || '测试失败')
        }
      } catch (error) {
        this.testResult = {
          success: false,
          error: error.message || '测试失败',
          message: error.response ? JSON.stringify(error.response.data, null, 2) : error.message
        }
        this.$Message.error('测试失败：' + (error.message || '未知错误'))
      } finally {
        this.testLoading = false
      }
    },

    // 测试取消
    handleTestCancel () {
      this.testModal = false
      this.testResult = null
    },

    // 格式化日期
    formatDate (date) {
      if (!date) return '-'
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },

    // 获取请求方式颜色
    getMethodColor (method) {
      const colors = {
        GET: 'blue',
        POST: 'green',
        PUT: 'orange',
        DELETE: 'red'
      }
      return colors[method] || 'default'
    },

    // 格式化JSON
    formatJson (data) {
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2)
        } catch (e) {
          return data
        }
      }
      return JSON.stringify(data, null, 2)
    }
  }
}
</script>

<style scoped>
.third-party-api-container {
  padding: 20px;
}

.search-bar {
  display: flex;
  align-items: center;
}

.test-container {
  max-height: 600px;
  overflow-y: auto;
}

.test-config {
  margin-bottom: 20px;
}

.test-result {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e8eaec;
}

.result-item {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.result-label {
  font-weight: bold;
  margin-right: 10px;
  min-width: 80px;
}

.result-data {
  margin-top: 10px;
  background-color: #f8f8f9;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  padding: 15px;
  max-height: 300px;
  overflow-y: auto;
}

.result-data pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 12px;
  line-height: 1.5;
}
</style>
